import { SkFont } from "@shopify/react-native-skia";
import { useCallback, useMemo } from "react";
import { Platform } from "react-native";

import ChartComparisonView from "./ChartComparisonView";

import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";

/**
 *
 */
interface Data extends Record<string, unknown> {
  [x: string]: string | number;
  season: string | number;
}

/**
 *
 */
interface Props {
  title: string;
  data?: Data[];
  yKeys?: string[];
  font?: SkFont | null;
  colors?: Record<string, string>;
  currentPlayerName: string;
  selectedPlayerName: string;
}

/**
 * Container component for chart-based player comparison
 * Handles theme-based styling and x-axis label formatting for mobile/desktop
 * @param root0 - Component props
 * @param root0.title - Chart title (e.g., "Carrying", "Dribbling")
 * @param root0.data - Array of data points with season and metric values
 * @param root0.yKeys - Array of metric keys to plot on the y-axis
 * @param root0.font - Skia font for chart text rendering
 * @param root0.colors - Color mapping for each metric line
 * @param root0.currentPlayerName - Name of the primary player being compared
 * @param root0.selectedPlayerName - Name of the player being compared against
 */
const ChartComparisonContainer = ({
  title,
  data,
  yKeys,
  font,
  colors,
  currentPlayerName,
  selectedPlayerName,
}: Props) => {
  const { resolvedTheme } = useTheme();
  const isMobile = useMobileScreen();
  const isNativeMobile = Platform.OS === "ios" || Platform.OS === "android";

  const labelColor = useMemo(
    () => (resolvedTheme === "dark" ? "#ffffff" : "#000000"),
    [resolvedTheme]
  );

  // Get all unique seasons/years from data in order
  const allSeasons = useMemo(() => {
    return (data ?? [])
      .map(d => d.season)
      .sort((a, b) => Number(a) - Number(b));
  }, [data]);

  // Format x-axis labels - show every other label on mobile for readability
  const formatXLabel = useCallback(
    (value: string | number) => {
      const strValue = String(value);

      // On mobile, show every other label to reduce clutter
      if (isMobile || isNativeMobile) {
        const seasonIndex = allSeasons.indexOf(value);
        // Hide odd-indexed labels (show 0, 2, 4, etc.)
        if (seasonIndex !== -1 && seasonIndex % 2 !== 0) {
          return "";
        }
      }

      // Handle integer years (e.g., 2019, 2020, 2021)
      if (Number.isInteger(Number(value))) {
        return value.toString();
      }

      // Handle season strings (e.g., "2019-20", "2020-21")
      const yearMatch = /^(\d{4})/.exec(strValue);
      if (yearMatch) {
        return strValue;
      }

      // Unknown format, don't show label
      return "";
    },
    [isMobile, isNativeMobile, allSeasons]
  );

  return (
    <ChartComparisonView
      title={title}
      data={data}
      yKeys={yKeys}
      font={font}
      colors={colors}
      currentPlayerName={currentPlayerName}
      selectedPlayerName={selectedPlayerName}
      labelColor={labelColor}
      formatXLabel={formatXLabel}
    />
  );
};

export default ChartComparisonContainer;
